
import { useState, useEffect } from "react";
import { Button } from "@opal/components";
import { useProjectsContext } from "@/app/providers/ProjectsContext";
import { useKeyPress } from "@/shared/hooks/useKeyPress";
import { InputVertical } from "@opal/layouts";
import { useAppRouter } from "@/shared/hooks/appNavigation";
import { useModal } from "@/shared/ui/refresh-components/contexts/ModalContext";
import { SvgFolderPlus } from "@opal/icons";
import Modal from "@/shared/ui/refresh-components/Modal";
import InputTypeIn from "@/shared/ui/refresh-components/inputs/InputTypeIn";
import { toast } from "@/shared/hooks/useToast";

interface CreateProjectModalProps {
  initialProjectName?: string;
}

export default function CreateProjectModal({
  initialProjectName,
}: CreateProjectModalProps) {
  const { createProject } = useProjectsContext();
  const modal = useModal();
  const route = useAppRouter();
  const [projectName, setProjectName] = useState(initialProjectName ?? "");

  // Reset when prop changes (modal reopens with different value)
  useEffect(() => {
    setProjectName(initialProjectName ?? "");
  }, [initialProjectName]);

  async function handleSubmit() {
    const name = projectName.trim();
    if (!name) return;

    try {
      const newProject = await createProject(name);
      route({ projectId: newProject.id });
      modal.toggle(false);
    } catch (e) {
      toast.error(`Failed to create the project ${name}`);
    }
  }

  useKeyPress(handleSubmit, "Enter");

  return (
    <>
      <Modal open={modal.isOpen} onOpenChange={modal.toggle}>
        <Modal.Content width="sm">
          <Modal.Header
            icon={SvgFolderPlus}
            title="Create New Project"
            description="Use projects to organize your files and chats in one place, and add custom instructions for ongoing work."
            onClose={() => modal.toggle(false)}
          />
          <Modal.Body>
            <InputVertical title="Project Name" withLabel>
              <InputTypeIn
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                placeholder="What are you working on?"
                showClearButton
              />
            </InputVertical>
          </Modal.Body>
          <Modal.Footer>
            <Button prominence="secondary" onClick={() => modal.toggle(false)}>
              Cancel
            </Button>
            <Button disabled={!projectName.trim()} onClick={handleSubmit}>
              Create Project
            </Button>
          </Modal.Footer>
        </Modal.Content>
      </Modal>
    </>
  );
}
